import FiltersService from "@/services/FiltersService";
import { defineStore, acceptHMRUpdate } from "pinia";
import { useAuthStore } from "./auth.store";
import { i18n } from '@/main';

export const useArchivalFiltersStore = defineStore({
  id: "archFilter",
  state: () => ({
    filters: {
      dateTimeStart: new Date(
        new Date().setDate(new Date().getDate() - 1)
      ).toISOString(),
      dateTimeEnd: new Date().toISOString(),
      orderByColumnName1: "createdAt",
      orderDirection1: "desc",
      taggedOnly: false,
    },
    locations: [],
    laneNumbers: [],
    vehicleCategories: [2, 3, 5, 7, 8, 9, 10, 11, i18n.global.t('lack')],
    vehicleData: {
      plateLpr: "registration",
      similarity: "similarity",
      minSpeed: "speed", // [km/h]
      maxSpeed: "speed", // [km/h]
      minTotalLoad: "short-total-weight", // kg
      maxTotalLoad: "short-total-weight", // kg
      minVehLength: "length", // cm
      maxVehLength: "length", // cm
      minVehHeight: "height", // cm
      maxVehHeight: "height", // cm
      minNumberOfAxles: "no-of-axles", // szt
      maxNumberOfAxles: "no-of-axles", // szt
      minOverloadSA: "single-axle", // %
      maxOverloadSA: "single-axle", // %
      minOverloadMA: "multiple-axle", // %
      maxOverloadMA: "multiple-axle", // %
      minOverloadTL: "dmc", // %
      maxOverloadTL: "dmc", // %
    },
    violations: {
      overload: i18n.global.t('permissible-load'),
      overheight: i18n.global.t('permissible-height'),
      overspeed: i18n.global.t('speed'),
      overlane: i18n.global.t('bypassing'),
      wrongDirection: i18n.global.t('wrong-way'),
    },
    orderByColumns: {
      createdAt: "measure-time",
      locationId: "location",
      plateLpr: "short-plate-no",
      speed: "speed",
      totalLoad: "total-weight",
      vehLength: "length",
      vehHeight: "height",
      numberOfAxles: "no-of-axles",
      ctg8p1: "vehicle-type",
      overloadSA: "single-axle",
      overloadMA: "multiple-axle",
      overloadTL: "dmc",
    },
    directionColumns: { desc: "ascending", asc: "descending" },
    authStore: useAuthStore(),
    loading: false,
    requireFields: [],
    error: null,
    vehicles: [],
    step: 1,
    lastStepBeforeResults: 1,
    shouldReload: false,
    isResult: false,
    savedLocations: [],
    savedFilters: {},
    savedVehicles: [],
  }),
  getters: {
    items: (state) => state.filters,
    selectedLocation: (state) => {
      if (state.filters.locations) {
        return state.locations.filter((location) => {
          return state.filters.locations.includes(location.id);
        });
      }
      return [];
    },
  },
  actions: {
    setShouldReload(value) {
      this.shouldReload = value;
    },
    setSavedState(locations, filters, vehicles) {
      this.savedLocations = [...locations];
      this.savedFilters = { ...filters };
      this.savedVehicles = [...vehicles];
    }, 
    setVehicles(vehicles) {
      this.vehicles = vehicles;
    },
    setStep(step) {
      this.step = step;
      if (step < 4) this.lastStepBeforeResults = step;
    },
      setLastStepBeforeResults(n) { this.lastStepBeforeResults = n; },
    setIsResult(val) {
      this.isResult = val;
    },
    async getLocations() {
      if (this.locations.length === 0) {
        try {
          const data = await FiltersService.getLocations();
          this.locations = data;

          const lanesSet = new Set();
          this.locations.forEach(location => {
            const laneCount = location.lanes.length;
            for (let i = 1; i <= laneCount; i++) {
              lanesSet.add(i);
            }
          });

          this.laneNumbers = [...lanesSet].sort((a, b) => a - b);
        } catch (error) {
          console.error("Błąd pobierania lokalizacji:", error);
        }
      }
    },
    checkAllLocations() {
      if (
        !this.filters.locations ||
        this.filters.locations.length !== this.locations.length
      ) {
        this.filters.locations = [];
        let filters = this.filters;
        this.locations.map((item) => {
          filters.locations.push(item.id);
        });
      } else {
        delete this.filters.locations;
      }
    },
    addItem(name, value) {
      // console.log("addItem archival", name, value);
      switch (name) {
        case "LocationList": {
          if (this.filters.locations) {
            if (!this.filters.locations.includes(value)) {
              this.filters.locations.push(value); //adding to array because value doesnt exists
            } else {
              this.filters.locations.splice(
                this.filters.locations.indexOf(value),
                1
              ); //deleting
            }
          } else {
            this.filters.locations = [value];
          }
          if (this.filters.locations.length == 0) {
            delete this.filters.locations;
          }

          break;
        }
        case "VehicleCategories": {
          if (this.filters.ctg8p1) {
            if (!this.filters.ctg8p1.includes(this.vehicleCategories[value])) {
              this.filters.ctg8p1.push(this.vehicleCategories[value]); //adding to array because value doesnt exists
            } else {
              this.filters.ctg8p1.splice(
                this.filters.ctg8p1.indexOf(this.vehicleCategories[value]),
                1
              ); //deleting
            }
          } else {
            this.filters.ctg8p1 = [this.vehicleCategories[value]];
          }
          break;
        }
        case "LaneNumbers": {
          if (this.filters.laneNumbers) {
            if (!this.filters.laneNumbers.includes(this.laneNumbers[value])) {
              this.filters.laneNumbers.push(this.laneNumbers[value]); //adding to array because value doesnt exists
            } else {
              this.filters.laneNumbers.splice(
                this.filters.laneNumbers.indexOf(this.laneNumbers[value]),
                1
              ); //deleting
            }
          } else {
            this.filters.laneNumbers = [this.laneNumbers[value]];
          }
          break;
        }
        case "ViolationsAll": {
          // console.log('jestem w ViolationsAll');
          let filters = this.filters;
          const allChecked = !Object.keys(this.violations).some(function (key) {
            return filters[key] === undefined;
          });

          if (!allChecked) {
            Object.keys(this.violations).map(function (key) {
              filters[key] = true;
            });
          } else {
            Object.keys(this.violations).map(function (key) {
              delete filters[key];
            });
          }
          break;
        }
        case "taggedOnly": {
          this.filters.taggedOnly = value;
          break;
        }        
        default: {
          // console.log('jestem tu');
          this.filters[name] = value;
        }
      }
    },
    removeItem(name) {
      switch (name) {
        case "rangeOfDate": {
          delete this.filters.dateTimeStart;
          delete this.filters.dateTimeEnd;
          break;
        }
        case "violations": {
          let filters = this.filters;
          Object.keys(this.violations).map(function (key) {
            delete filters[key];
          });
          break;
        }
        case "vehicleData": {
          delete this.filters.plateLpr;
          delete this.filters.similarity;
          delete this.filters.minSpeed;
          delete this.filters.maxSpeed;
          delete this.filters.minTotalLoad;
          delete this.filters.maxTotalLoad;
          delete this.filters.minVehLength;
          delete this.filters.maxVehLength;
          delete this.filters.minVehHeight;
          delete this.filters.maxVehHeight;
          delete this.filters.minNumberOfAxles;
          delete this.filters.maxNumberOfAxles;
          delete this.filters.minOverloadSA;
          delete this.filters.maxOverloadSA;
          delete this.filters.minOverloadMA;
          delete this.filters.maxOverloadMA;
          delete this.filters.minOverloadTL;
          delete this.filters.maxOverloadTL;
          break;
        }
        default: {
          delete this.filters[name];
        }
      }
    },
    checkRequireData() {
      this.requireFields = [];
      this.error = false;
      this.checkRequireData1();
      this.checkRequireData2();
      this.checkRequireData3();
    },
    checkRequireData1(withClear) {
      if (withClear) {
        this.requireFields = [];
        this.error = false;
      }

      if (!this.filters.dateTimeStart) {
        this.requireFields.push("dateTimeStart");
      }
      if (!this.filters.dateTimeEnd) {
        this.requireFields.push("dateTimeEnd");
      }
      if (!this.filters.locations) {
        this.requireFields.push("locations");
      }

      if (this.requireFields.length) {
        this.error = true;
        this.setErrors(i18n.global.t('required-parameter-missing'));
      }
    },
    checkRequireData2(withClear) {
      if (withClear) {
        this.requireFields = [];
        this.error = false;
      }

      if (this.requireFields.length) {
        this.error = true;
        this.setErrors(i18n.global.t('required-parameter-missing'));
      }
    },
    checkRequireData3(withClear) {
      if (withClear) {
        this.requireFields = [];
        this.error = false;
      }

      if (this.requireFields.length) {
        this.error = true;
        this.setErrors(i18n.global.t('required-parameter-missing'));
      }
    },
    setErrors(error) {
      this.authStore.errorMessage = error;
    },
  },
});

if (import.meta.hot) {
  import.meta.hot.accept(
    acceptHMRUpdate(useArchivalFiltersStore, import.meta.hot)
  );
}